import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TreatmentPlanReviewForm from './TreatmentPlanReviewForm';
import TreatmentPlanReviewChart, { processChartData } from './TreatmentPlanReviewChart';
import TreatmentPlanReviewAnalytics from './TreatmentPlanReviewAnalytics';
import TreatmentPlanReviewPDF from './TreatmentPlanReviewPDF';
import { analyticsDataService } from '../../../services/analyticsDataService';

const CHART_TYPE = 'treatment-plan-review';

const TreatmentPlanReview = ({ user, viewData = null, readOnly = false }) => {
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const pdfChartRef = useRef(null);
  const [view, setView] = useState(viewData ? 'chart' : 'select'); // 'select', 'form', 'chart', 'analytics'
  const [savedData, setSavedData] = useState(
    viewData ? { data: viewData, timestamp: new Date().toISOString(), recordCount: viewData.length } : null
  );
  const [hasExistingData, setHasExistingData] = useState(!!(viewData && viewData.length));

  // Memoize processed chart data so it's computed once and shared
  const chartData = useMemo(() => {
    if (savedData?.data) return processChartData(savedData.data);
    return [];
  }, [savedData]);

  // Initialize data service with user ID and check for existing data
  useEffect(() => {
    if (viewData) return; // read-only supervisor view — data is injected via props
    if (user?.id) {
      analyticsDataService.setCurrentUser(user.id);
    }
    checkExistingData();

    // Cloud sync: pull newer data from cloud
    if (user?.id) {
      analyticsDataService.syncFromCloud(CHART_TYPE).then(cloudData => {
        if (cloudData) {
          setSavedData(cloudData);
          setHasExistingData(true);
        }
      });
    }
  }, [user, viewData]);

  const checkExistingData = () => {
    const existing = analyticsDataService.hasChartData(CHART_TYPE);
    setHasExistingData(existing);
    if (existing) {
      const data = analyticsDataService.getChartData(CHART_TYPE);
      setSavedData(data);
    }
  };

  const handleFormSave = (data) => {
    const result = analyticsDataService.saveChartData(CHART_TYPE, data);
    if (result.success) {
      setSavedData({ data, timestamp: result.timestamp, recordCount: result.recordCount });
      setHasExistingData(true);
      setView('chart'); // Switch to chart view after saving
    }
  };

  const handleFormCancel = () => {
    setView('select');
  };

  return (
    <div className="h-full overflow-y-auto p-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        {!readOnly && (
        <button
          onClick={() => navigate('/analytics')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <span className="mr-2">←</span>
          Back to Charts
        </button>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Treatment Plan Review Tracker</h1>
          <p className="text-gray-600 mt-2">Monitor treatment plan review schedules and track compliance timelines</p>
        </div>

        {/* Action Buttons - always visible */}
        <div className="flex gap-4 mb-8">
            {!readOnly && (
            <button
              onClick={() => setView('form')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                view === 'form'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Enter New Data
            </button>
            )}
            <button
              onClick={() => setView('chart')}
              disabled={!hasExistingData}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                view === 'chart'
                  ? 'bg-blue-600 text-white shadow-md'
                  : !hasExistingData
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Visualize Data
              {hasExistingData && savedData && (
                <span className="ml-2 text-xs opacity-75">
                  ({savedData.recordCount} records)
                </span>
              )}
            </button>
            <button
              onClick={() => setView('analytics')}
              disabled={!hasExistingData}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                view === 'analytics'
                  ? 'bg-blue-600 text-white shadow-md'
                  : !hasExistingData
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              View Analytics
            </button>
            <TreatmentPlanReviewPDF
              chartData={chartData}
              chartRef={pdfChartRef}
              user={user}
            />
          </div>

        {/* Content Area */}
        {view === 'select' && (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <p className="text-gray-500">Select an option above to get started</p>
            {hasExistingData && savedData && (
              <p className="text-sm text-gray-400 mt-2">
                Last updated: {new Date(savedData.timestamp).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {view === 'form' && (
          <TreatmentPlanReviewForm
            onSave={handleFormSave}
            onCancel={handleFormCancel}
            existingData={savedData?.data || []}
          />
        )}

        {view === 'chart' && savedData && (
          <div>
            <div ref={chartRef}>
              <TreatmentPlanReviewChart data={savedData.data} />
            </div>
            <p className="text-sm text-gray-400 mt-4 text-center">
              {savedData.recordCount} records | Last updated: {new Date(savedData.timestamp).toLocaleString()}
            </p>
          </div>
        )}

        {view === 'analytics' && savedData && (
          <div>
            <TreatmentPlanReviewAnalytics chartData={chartData} />
            <p className="text-sm text-gray-400 mt-4 text-center">
              {savedData.recordCount} records | Last updated: {new Date(savedData.timestamp).toLocaleString()}
            </p>
          </div>
        )}

        {/* Hidden chart for PDF capture - always rendered when data exists */}
        {savedData && (
          <div
            ref={pdfChartRef}
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: `${Math.max(1200, (savedData.data?.length || 0) * 32)}px`,
              minHeight: '600px'
            }}
            aria-hidden="true"
          >
            <TreatmentPlanReviewChart data={savedData.data} showLegend={false} chartHeight={550} pdfMode={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TreatmentPlanReview;
